import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { addWorkdays, fromISODate, calendarDaysBetween } from './workdays';
import type { PdfDesign } from './pdfDesign';
import type { QuotationDetail, QuotationProcess } from './NewQuotationModal';
import type { OrderPart } from './OrderPartModal';

// ============================================================================
//  Public types — unchanged; callers in CreatePdfView depend on this surface.
// ============================================================================

export interface CompanyInfo {
  logoPath: string | null;
  name: string | null;
  legalName: string | null;
  addressCountry: string | null;
  addressCounty: string | null;
  addressStreet: string | null;
  addressZip: string | null;
}

export interface OrderInfo {
  orderUid: string;
  customerName: string | null;
  customerLogo: string | null;
  contactName: string | null;
  technicianName: string | null;
}

export interface CustomerInfo {
  name: string;
  legalName: string | null;
  address: { country: string | null; county: string | null; street: string | null; zip: string | null };
  legal: { companyName: string | null; vatNumber: string | null };
}

export interface ResolvedRef {
  equipmentById: Map<number, { id: number; name: string; imagePath: string | null; accessories?: Array<{ id: number; name: string }> }>;
  consumableById: Map<number, { id: number; name: string; imagePath: string | null }>;
  accessoryById: Map<number, { id: number; name: string }>;
  technicianById: Map<number, { id: number; name: string }>;
}

// ============================================================================
//  Design tokens — single palette/typography source, resolved from Mantine.
//  RGB tuples mirror the .section-header / orange-6 look used by the UI so
//  the PDF shares the same visual identity as the on-screen Quotation modal.
// ============================================================================

type RGB = [number, number, number];

const PALETTE: Record<
  | 'orange' | 'orangeLight' | 'orangeText'
  | 'gray1' | 'gray3' | 'gray6' | 'gray8'
  | 'body' | 'white'
  | 'blue' | 'blueLight' | 'blueText'
  | 'green' | 'greenLight' | 'greenText',
  RGB
> = {
  orange:      [253, 126, 20 ],  // mantine orange-6
  orangeLight: [255, 244, 230],  // mantine orange-0
  orangeText:  [204, 85,  0  ],  // darker orange for light-orange badge text
  gray1:       [241, 243, 245],  // mantine gray-1  (section-header bg)
  gray3:       [222, 226, 230],  // mantine gray-3  (borders)
  gray6:       [134, 142, 150],  // mantine gray-6  (dimmed)
  gray8:       [52,  58,  64 ],  // mantine gray-8  (section-header text)
  body:        [33,  37,  41 ],  // primary body text
  white:       [255, 255, 255],
  blue:        [51,  154, 240],  // mantine blue-5  (shipping fill)
  blueLight:   [231, 245, 255],  // mantine blue-0
  blueText:    [28,  126, 214],  // darker blue for light-blue badge text
  green:       [64,  192, 87 ],  // mantine green-6
  greenLight:  [235, 251, 238],  // mantine green-0
  greenText:   [47,  158, 68 ],  // darker green for light-green badge text
};

const TYPE = {
  h1: 20, h2: 13, h3: 11,
  body: 10, small: 9, xs: 8,
};

const PAGE = {
  margin: 36,
  bottomLimit: 796,  // call addPage() when y > this during manual drawing
};

// ============================================================================
//  Low-level jspdf wrappers (tuple unpacking keeps call-sites quiet)
// ============================================================================

function setFill(pdf: jsPDF, rgb: RGB)   { pdf.setFillColor(rgb[0], rgb[1], rgb[2]); }
function setStroke(pdf: jsPDF, rgb: RGB) { pdf.setDrawColor(rgb[0], rgb[1], rgb[2]); }
function setText(pdf: jsPDF, rgb: RGB)   { pdf.setTextColor(rgb[0], rgb[1], rgb[2]); }

// ============================================================================
//  Image prefetch — all remote images become data URLs (with dims) up front.
//  `didDrawCell` hooks inside autoTable are sync and can't await, so every
//  image the renderer might want must be in memory before drawing starts.
// ============================================================================

async function loadImageAsDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? '') || null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

async function probeImageDims(dataUrl: string): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

interface ImageRec { dataUrl: string; w: number; h: number }

async function prefetchImages(
  paths: Array<string | null | undefined>,
  imageUrlFor: (p: string | null | undefined) => string | undefined,
): Promise<Map<string, ImageRec>> {
  const uniq = Array.from(new Set(paths.filter((p): p is string => !!p)));
  const entries = await Promise.all(uniq.map(async (path) => {
    const url = imageUrlFor(path);
    if (!url) return [path, null] as const;
    const dataUrl = await loadImageAsDataUrl(url);
    if (!dataUrl) return [path, null] as const;
    const dims = await probeImageDims(dataUrl);
    if (!dims) return [path, null] as const;
    return [path, { dataUrl, w: dims.w, h: dims.h } satisfies ImageRec] as const;
  }));
  const out = new Map<string, ImageRec>();
  for (const [path, rec] of entries) if (rec) out.set(path, rec);
  return out;
}

// ============================================================================
//  Formatting helpers (kept from previous revision for behavior parity)
// ============================================================================

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dayjs(iso).format('YYYY-MM-DD');
}
function fmtMoney(n: number | null | undefined): string {
  if (n == null) return '—';
  return `€ ${n.toFixed(2)}`;
}

function processTypeName(p: QuotationProcess, design: PdfDesign, refs: ResolvedRef): string {
  if (p.entryType === 'Equipment') {
    const eq = p.equipmentId ? refs.equipmentById.get(p.equipmentId) : undefined;
    return eq?.name ?? 'Equipment';
  }
  if (p.entryType === 'Labour') {
    if (!design.technicians.includeNames) return 'Technician';
    const t = p.technicianId ? refs.technicianById.get(p.technicianId) : undefined;
    return t?.name ?? 'Technician';
  }
  if (p.entryType === 'Consumable') {
    const c = p.consumableId ? refs.consumableById.get(p.consumableId) : undefined;
    return c?.name ?? 'Consumable';
  }
  return p.entryType;
}

interface DetailsBlock { primary: string; extras: string[] }

function processDetails(p: QuotationProcess, design: PdfDesign, refs: ResolvedRef): DetailsBlock {
  const primary = processTypeName(p, design, refs);
  const extras: string[] = [];
  if (p.entryType === 'Equipment') {
    if (design.equipment.includeAccessories && p.accessories?.length) {
      const labels = p.accessories
        .map((a) => {
          const acc = a.accessoryId ? refs.accessoryById.get(a.accessoryId) : undefined;
          return acc ? `${acc.name} (${a.hours}h)` : null;
        })
        .filter((x): x is string => !!x);
      if (labels.length) extras.push(`Accessories: ${labels.join(', ')}`);
    }
    if (design.equipment.includeEquipmentConsumables && p.consumables?.length) {
      const labels = p.consumables
        .map((c) => {
          const item = c.consumableId ? refs.consumableById.get(c.consumableId) : undefined;
          return item ? `${item.name} × ${c.quantity}` : null;
        })
        .filter((x): x is string => !!x);
      if (labels.length) extras.push(`Consumables: ${labels.join(', ')}`);
    }
  }
  if (p.entryType === 'Labour' && design.technicians.includeNames && p.labourDescription) {
    extras.push(p.labourDescription);
  }
  return { primary, extras };
}

// ============================================================================
//  Drawing helpers — cards, section bands, badges
// ============================================================================

function drawCard(
  pdf: jsPDF, x: number, y: number, w: number, h: number,
  fill: RGB | null = PALETTE.white, stroke: RGB | null = PALETTE.gray3,
) {
  pdf.setLineWidth(0.5);
  if (fill && stroke) {
    setFill(pdf, fill); setStroke(pdf, stroke);
    pdf.roundedRect(x, y, w, h, 3, 3, 'FD');
  } else if (fill) {
    setFill(pdf, fill);
    pdf.roundedRect(x, y, w, h, 3, 3, 'F');
  } else if (stroke) {
    setStroke(pdf, stroke);
    pdf.roundedRect(x, y, w, h, 3, 3, 'S');
  }
}

/** Banded section header with a 3pt orange accent on the left; returns y below band. */
function drawSectionHeader(pdf: jsPDF, y: number, title: string, width: number): number {
  const x = PAGE.margin;
  const h = 22;
  setFill(pdf, PALETTE.gray1);
  pdf.roundedRect(x, y, width, h, 3, 3, 'F');
  // Orange left accent
  setFill(pdf, PALETTE.orange);
  pdf.rect(x, y, 3, h, 'F');
  // Bottom hairline
  setStroke(pdf, PALETTE.gray3);
  pdf.setLineWidth(0.5);
  pdf.line(x, y + h, x + width, y + h);
  // Title
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(TYPE.small);
  setText(pdf, PALETTE.gray8);
  pdf.text(title.toUpperCase(), x + 12, y + 14, { charSpace: 0.4 });
  // Reset
  setText(pdf, PALETTE.body);
  pdf.setFont('helvetica', 'normal');
  return y + h;
}

type BadgeVariant = 'light-gray' | 'light-orange' | 'light-blue' | 'light-green' | 'filled-blue';

function badgePalette(variant: BadgeVariant): { fill: RGB; text: RGB } {
  switch (variant) {
    case 'light-gray':   return { fill: PALETTE.gray1,       text: PALETTE.gray8 };
    case 'light-orange': return { fill: PALETTE.orangeLight, text: PALETTE.orangeText };
    case 'light-blue':   return { fill: PALETTE.blueLight,   text: PALETTE.blueText };
    case 'light-green':  return { fill: PALETTE.greenLight,  text: PALETTE.greenText };
    case 'filled-blue':  return { fill: PALETTE.blue,        text: PALETTE.white };
  }
}

/** Rounded-pill badge. Computes its own width from label; returns it for chaining. */
function drawBadge(pdf: jsPDF, x: number, y: number, label: string, variant: BadgeVariant): number {
  const prevFont = pdf.getFont();
  const prevSize = pdf.getFontSize();
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(TYPE.xs);
  const textW = pdf.getTextWidth(label);
  const padX = 6;
  const w = textW + padX * 2;
  const h = 13;
  const p = badgePalette(variant);
  setFill(pdf, p.fill);
  pdf.roundedRect(x, y, w, h, 3, 3, 'F');
  setText(pdf, p.text);
  pdf.text(label, x + w / 2, y + 9, { align: 'center', baseline: 'alphabetic' });
  // Restore
  pdf.setFont(prevFont.fontName, prevFont.fontStyle);
  pdf.setFontSize(prevSize);
  setText(pdf, PALETTE.body);
  return w;
}

/** Computes the width drawBadge would produce at the current font — without drawing. */
function measureBadgeWidth(pdf: jsPDF, label: string): number {
  const prevFont = pdf.getFont();
  const prevSize = pdf.getFontSize();
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(TYPE.xs);
  const textW = pdf.getTextWidth(label);
  pdf.setFont(prevFont.fontName, prevFont.fontStyle);
  pdf.setFontSize(prevSize);
  return textW + 12;
}

// ============================================================================
//  Document header (top strip with title + status badge)
// ============================================================================

function drawDocHeader(
  pdf: jsPDF, quotation: QuotationDetail, margin: number, pageWidth: number,
): number {
  const y0 = margin;
  // Left accent bar
  setFill(pdf, PALETTE.orange);
  pdf.rect(margin, y0, 4, 26, 'F');
  // Title
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(TYPE.h1);
  setText(pdf, PALETTE.body);
  pdf.text(`Quotation ${quotation.quotationUid}`, margin + 12, y0 + 18);
  // Timestamp (top-right)
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(TYPE.xs);
  setText(pdf, PALETTE.gray6);
  pdf.text(`Generated ${dayjs().format('YYYY-MM-DD HH:mm')}`, pageWidth - margin, y0 + 18, { align: 'right' });
  return y0 + 32;
}

// ============================================================================
//  Party blocks (two-column From / In the attention of)
// ============================================================================

function drawPartyCard(
  pdf: jsPDF, x: number, y: number, width: number,
  label: string | null,   // null → no label row drawn (card still always rendered)
  logo: ImageRec | null,
  title: string | null,
  subtitle: string | null,
  addressLines: string[],
): number {
  const padX = 10;
  const padTop = 10;
  const cx = x + padX;
  // When there IS a label row, gap between label and the next element depends on whether
  // a logo follows: logo provides natural separation (6pt), without a logo we widen to
  // 16pt to prevent the 8pt label from visually colliding with the 11pt title.
  const labelGap = logo ? 6 : 16;

  let h = padTop;
  if (label) {
    h += 10;                           // label row
    if (!logo) h += 6;                 // extra clearance when no logo (prevents text overlap)
  } else if (!logo) {
    h += 6;                            // small top-pad when starting directly with title/name
  }
  if (logo) h += 52;                   // 40pt logo slot + 12pt ascender clearance
  if (title) h += 14;                  // title row
  if (subtitle) h += 12;              // subtitle row
  h += addressLines.length * 12;      // address rows
  h += padTop;
  drawCard(pdf, x, y, width, h, PALETTE.white, PALETTE.gray3);

  let cy = y + padTop + 8;
  if (label) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(TYPE.xs);
    setText(pdf, PALETTE.gray6);
    pdf.text(label.toUpperCase(), cx, cy, { charSpace: 0.4 });
    cy += labelGap;
  } else if (!logo) {
    cy += 6;
  }

  if (logo) {
    const boxW = 72, boxH = 40;
    const scale = Math.min(boxW / logo.w, boxH / logo.h);
    const dw = logo.w * scale, dh = logo.h * scale;
    const offX = (boxW - dw) / 2, offY = (boxH - dh) / 2;
    try { pdf.addImage(logo.dataUrl, cx + offX, cy + offY, dw, dh, undefined, 'FAST'); } catch { /* ignore */ }
    cy += 52; // logo (40) + ascender clearance so the following title doesn't ride up
  }

  if (title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(TYPE.h3);
    setText(pdf, PALETTE.body);
    pdf.text(title, cx, cy);
    cy += 14;
  }

  if (subtitle) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(TYPE.small);
    setText(pdf, PALETTE.gray6);
    pdf.text(subtitle, cx, cy);
    cy += 12;
  }

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(TYPE.small);
  setText(pdf, PALETTE.body);
  for (const line of addressLines) { pdf.text(line, cx, cy); cy += 12; }

  return y + h;
}

function drawCompanyBlock(
  pdf: jsPDF, x: number, y: number, width: number,
  company: CompanyInfo, technicianName: string | null, logo: ImageRec | null,
): number {
  const addr = [
    company.addressStreet,
    [company.addressCounty, company.addressZip].filter(Boolean).join(' ').trim() || null,
    company.addressCountry,
  ].filter((s): s is string => !!s);
  return drawPartyCard(
    pdf, x, y, width,
    technicianName ? `From · ${technicianName}` : null,
    logo,
    company.name,
    company.legalName,
    addr,
  );
}

function drawCustomerBlock(
  pdf: jsPDF, x: number, y: number, width: number,
  order: OrderInfo, customer: CustomerInfo | null, logo: ImageRec | null,
): number {
  const addr = customer ? [
    customer.address.street,
    [customer.address.county, customer.address.zip].filter(Boolean).join(' ').trim() || null,
    customer.address.country,
  ].filter((s): s is string => !!s) : [];
  // Prefer VAT as subtitle; fall back to legal company name (if distinct).
  const subtitle = customer?.legal.vatNumber
    ? `VAT: ${customer.legal.vatNumber}`
    : (customer?.legal.companyName && customer.legal.companyName !== customer.name ? customer.legal.companyName : null);
  return drawPartyCard(
    pdf, x, y, width,
    order.contactName ? `In the attention of · ${order.contactName}` : null,
    logo,
    customer?.name ?? order.customerName,
    subtitle,
    addr,
  );
}

// ============================================================================
//  Part block — header card + inset process sub-table
// ============================================================================

function drawPlaceholderImage(pdf: jsPDF, x: number, y: number, size: number, letter: string) {
  setFill(pdf, PALETTE.gray1);
  setStroke(pdf, PALETTE.gray3);
  pdf.setLineWidth(0.5);
  pdf.roundedRect(x, y, size, size, 3, 3, 'FD');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  setText(pdf, PALETTE.gray6);
  pdf.text((letter || '·').toUpperCase(), x + size / 2, y + size / 2 + 5, { align: 'center' });
  setText(pdf, PALETTE.body);
}

interface PartBlockCtx {
  part: OrderPart | null;
  qpart: QuotationDetail['parts'][number];
  design: PdfDesign;
  refs: ResolvedRef;
  imageCache: Map<string, ImageRec>;
  zebra: boolean;
  margin: number;
  innerWidth: number;
}

function variantForEntryType(entryType: QuotationProcess['entryType']): BadgeVariant {
  if (entryType === 'Equipment') return 'light-orange';
  if (entryType === 'Labour') return 'light-blue';
  return 'light-green';
}

function drawPartBlock(
  pdf: jsPDF, yIn: number, ctx: PartBlockCtx,
  ensureSpace: (y: number, need: number) => number,
): number {
  const { part, qpart, design, refs, imageCache, zebra, margin, innerWidth } = ctx;

  const showProcs = design.basics.includeProcessesPerPart && qpart.processes.length > 0;
  const headerH = 60;

  // Measure approximate sub-table height for pre-flight page-break.
  let subTableApproxH = 0;
  if (showProcs) {
    let accum = 24;
    for (const pr of qpart.processes) {
      const extras = processDetails(pr, design, refs).extras.length;
      accum += 22 + extras * 10;
    }
    subTableApproxH = accum;
  }
  const need = headerH + (showProcs ? subTableApproxH + 4 : 0) + 8;
  let y = ensureSpace(yIn, need);

  // Header card background (zebra tint for odd index)
  const cardX = margin;
  const cardW = innerWidth;
  const cardFill = zebra ? PALETTE.orangeLight : PALETTE.white;
  drawCard(pdf, cardX, y, cardW, headerH, cardFill, PALETTE.gray3);

  // Image slot (letterboxed, with white backing so it stays crisp on zebra tint)
  const imgSize = 36;
  const imgX = cardX + 10;
  const imgY = y + (headerH - imgSize) / 2;
  let textX = cardX + 10;
  if (design.basics.includePartsIcon) {
    const img = part?.imagePath ? imageCache.get(part.imagePath) ?? null : null;
    if (img) {
      setFill(pdf, PALETTE.white);
      setStroke(pdf, PALETTE.gray3);
      pdf.setLineWidth(0.5);
      pdf.roundedRect(imgX, imgY, imgSize, imgSize, 3, 3, 'FD');
      const scale = Math.min(imgSize / img.w, imgSize / img.h);
      const dw = img.w * scale, dh = img.h * scale;
      const offX = (imgSize - dw) / 2, offY = (imgSize - dh) / 2;
      try { pdf.addImage(img.dataUrl, imgX + offX, imgY + offY, dw, dh, undefined, 'FAST'); } catch { /* ignore */ }
    } else {
      drawPlaceholderImage(pdf, imgX, imgY, imgSize, (part?.name ?? '?').charAt(0));
    }
    textX = imgX + imgSize + 10;
  }

  // Name + UID (left block)
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(TYPE.h3);
  setText(pdf, PALETTE.body);
  const nameMaxW = cardW - (textX - cardX) - 160;
  const nameLines = pdf.splitTextToSize(part?.name ?? '(deleted part)', Math.max(80, nameMaxW)) as string[];
  pdf.text(nameLines[0] ?? '', textX, y + 24);
  pdf.setFont('courier', 'normal');
  pdf.setFontSize(TYPE.xs);
  setText(pdf, PALETTE.gray6);
  pdf.text(part?.partUid ?? '', textX, y + 40);
  pdf.setFont('helvetica', 'normal');
  setText(pdf, PALETTE.body);

  // Right side: qty badge + optional inline totals
  const rightEdge = cardX + cardW - 10;
  const qtyLabel = `Qty ${qpart.quantity}`;
  const qtyW = measureBadgeWidth(pdf, qtyLabel);
  drawBadge(pdf, rightEdge - qtyW, y + 8, qtyLabel, 'light-gray');

  // Total price is always shown in the part header — even when the process sub-table is
  // visible below, the customer still needs to see the part's total at a glance.
  {
    const cost = qpart.processes.reduce((s, p) => s + (p.cost ?? 0), 0);
    const price = qpart.processes.reduce((s, p) => s + (p.price ?? 0), 0);
    const markup = cost > 0 ? ((price / cost - 1) * 100) : null;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(TYPE.h3);
    setText(pdf, PALETTE.body);
    pdf.text(fmtMoney(price), rightEdge, y + 40, { align: 'right' });
    // Markup detail only shown when processes are hidden (Open Book) — when the sub-table
    // is present the per-row markup columns already carry that information.
    if (!showProcs && design.markups.includeMarkups) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(TYPE.xs);
      setText(pdf, PALETTE.gray6);
      const tags: string[] = [`Cost ${fmtMoney(cost)}`];
      if (markup != null) tags.push(`Markup ${markup.toFixed(0)}%`);
      pdf.text(tags.join('  ·  '), rightEdge, y + 52, { align: 'right' });
    }
    setText(pdf, PALETTE.body);
    pdf.setFont('helvetica', 'normal');
  }

  y += headerH;

  if (showProcs) {
    y = drawProcessSubTable(pdf, y + 2, qpart, design, refs, imageCache);
  }

  return y + 8;
}

function drawProcessSubTable(
  pdf: jsPDF, y: number,
  qpart: QuotationDetail['parts'][number],
  design: PdfDesign,
  refs: ResolvedRef,
  imageCache: Map<string, ImageRec> = new Map(),
): number {
  const hasMarkupCols = design.markups.includeMarkupsForEach;
  const head = hasMarkupCols
    ? [['Type', 'Details', 'Qty', 'Markup', 'Cost', 'Price']]
    : [['Type', 'Details', 'Qty']];

  // Pre-compute per-row metadata used by willDrawCell / didDrawCell.
  const typeBadges: string[] = [];
  const typeVariants: BadgeVariant[] = [];
  const detailsBlocks: DetailsBlock[] = [];
  const body: string[][] = qpart.processes.map((pr) => {
    const badge = pr.entryType;                // "Equipment" / "Labour" / "Consumable"
    typeBadges.push(badge);
    typeVariants.push(variantForEntryType(pr.entryType));
    const block = processDetails(pr, design, refs);
    detailsBlocks.push(block);
    // Type cell raw text: spacer wide enough to reserve badge height in the row.
    const typeSpacer = ' '.repeat(Math.max(10, badge.length + 4));
    const detailsText = [block.primary, ...block.extras].join('\n');
    if (hasMarkupCols) {
      return [
        typeSpacer,
        detailsText,
        String(pr.mainQuantity),
        `${pr.markupPct}%`,
        fmtMoney(pr.cost),
        fmtMoney(pr.price),
      ];
    }
    return [typeSpacer, detailsText, String(pr.mainQuantity)];
  });

  const margin = PAGE.margin;
  const leftInset = 60;

  autoTable(pdf, {
    startY: y,
    margin: { left: margin + leftInset, right: margin },
    theme: 'plain',
    head,
    body,
    styles: {
      fontSize: TYPE.small,
      cellPadding: { top: 6, bottom: 6, left: 6, right: 6 },
      valign: 'top',
      textColor: PALETTE.body,
      lineColor: PALETTE.gray3,
      lineWidth: 0,
    },
    headStyles: {
      fillColor: PALETTE.gray1,
      textColor: PALETTE.gray8,
      fontSize: TYPE.xs,
      fontStyle: 'bold',
      cellPadding: { top: 5, bottom: 5, left: 6, right: 6 },
    },
    columnStyles: hasMarkupCols
      ? {
          0: { cellWidth: 90 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 42, halign: 'right' },
          3: { cellWidth: 52, halign: 'right' },
          4: { cellWidth: 60, halign: 'right' },
          5: { cellWidth: 60, halign: 'right', fontStyle: 'bold' },
        }
      : {
          0: { cellWidth: 90 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 42, halign: 'right' },
        },
    willDrawCell: (data) => {
      if (data.section !== 'body') return;
      if (data.column.index === 0 || data.column.index === 1) {
        // Suppress default text rendering — our didDrawCell draws styled output.
        data.cell.text = [];
      }
    },
    didDrawCell: (data) => {
      if (data.section !== 'body') return;
      const rowIdx = data.row.index;
      if (data.column.index === 0) {
        const label = typeBadges[rowIdx] ?? '';
        const variant = typeVariants[rowIdx] ?? 'light-gray';
        const cellX = data.cell.x + 6;
        const cellY = data.cell.y + (data.cell.height - 13) / 2;
        drawBadge(pdf, cellX, cellY, label, variant);
      } else if (data.column.index === 1) {
        const block = detailsBlocks[rowIdx];
        if (!block) return;
        const pr = qpart.processes[rowIdx];
        let textOffsetX = 6;

        // Equipment icon: draw a small image (or placeholder) to the left of the text when
        // includeEquipmentIcons is enabled and the process is an Equipment entry.
        if (design.equipment.includeEquipmentIcons && pr?.entryType === 'Equipment' && pr.equipmentId) {
          const eq = refs.equipmentById.get(pr.equipmentId);
          const iconSz = Math.min(data.cell.height - 8, 20);
          const iconX = data.cell.x + 6;
          const iconY = data.cell.y + (data.cell.height - iconSz) / 2;
          const img = eq?.imagePath ? imageCache.get(eq.imagePath) ?? null : null;
          if (img) {
            setFill(pdf, PALETTE.white);
            setStroke(pdf, PALETTE.gray3);
            pdf.setLineWidth(0.4);
            pdf.roundedRect(iconX, iconY, iconSz, iconSz, 2, 2, 'FD');
            const scale = Math.min(iconSz / img.w, iconSz / img.h);
            const dw = img.w * scale, dh = img.h * scale;
            try { pdf.addImage(img.dataUrl, iconX + (iconSz - dw) / 2, iconY + (iconSz - dh) / 2, dw, dh, undefined, 'FAST'); } catch { /* ignore */ }
          } else {
            // Placeholder square with first letter
            setFill(pdf, PALETTE.gray1);
            setStroke(pdf, PALETTE.gray3);
            pdf.setLineWidth(0.4);
            pdf.roundedRect(iconX, iconY, iconSz, iconSz, 2, 2, 'FD');
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(6);
            setText(pdf, PALETTE.gray6);
            pdf.text((eq?.name ?? '?').charAt(0).toUpperCase(), iconX + iconSz / 2, iconY + iconSz / 2 + 2, { align: 'center' });
          }
          textOffsetX += iconSz + 6;
        }

        const cx = data.cell.x + textOffsetX;
        let cy = data.cell.y + 12;
        const maxW = data.cell.width - textOffsetX - 6;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(TYPE.small);
        setText(pdf, PALETTE.body);
        const primaryLines = pdf.splitTextToSize(block.primary, maxW) as string[];
        for (const l of primaryLines) { pdf.text(l, cx, cy); cy += 11; }
        if (block.extras.length) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(TYPE.xs);
          setText(pdf, PALETTE.gray6);
          for (const line of block.extras) {
            const wrap = pdf.splitTextToSize(line, maxW) as string[];
            for (const l of wrap) { pdf.text(l, cx, cy); cy += 10; }
          }
        }
        setText(pdf, PALETTE.body);
        pdf.setFont('helvetica', 'normal');
      }
    },
  });

  return (pdf as any).lastAutoTable?.finalY ?? y;
}

// ============================================================================
//  Shipping row + totals strip
// ============================================================================

function drawShippingRow(pdf: jsPDF, y: number, margin: number, width: number, amount: number): number {
  const h = 24;
  setFill(pdf, PALETTE.blueLight);
  pdf.roundedRect(margin, y, width, h, 3, 3, 'F');
  // SHIPPING badge
  const bx = margin + 10;
  const by = y + (h - 13) / 2;
  const bw = drawBadge(pdf, bx, by, 'SHIPPING', 'filled-blue');
  // Amount (bold)
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(TYPE.h3);
  setText(pdf, PALETTE.body);
  pdf.text(fmtMoney(amount), bx + bw + 10, y + 16);
  setText(pdf, PALETTE.body);
  return y + h;
}

function drawTotalsStrip(
  pdf: jsPDF, y: number, margin: number, width: number,
  quotation: QuotationDetail, design: PdfDesign,
): number {
  const rightX = margin + width - 4;
  // Orange 1pt rule above (partial width, right-aligned)
  setStroke(pdf, PALETTE.orange);
  pdf.setLineWidth(1);
  pdf.line(margin + width - 260, y, margin + width, y);
  let cy = y + 16;
  if (design.markups.includeMarkups) {
    const totalCost = quotation.totalCost ?? 0;
    const totalPrice = quotation.totalPrice ?? 0;
    const markup = totalCost > 0 ? ((totalPrice / totalCost - 1) * 100) : null;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(TYPE.small);
    setText(pdf, PALETTE.gray6);
    pdf.text('Total cost', rightX - 140, cy);
    setText(pdf, PALETTE.body);
    pdf.text(fmtMoney(totalCost), rightX, cy, { align: 'right' });
    cy += 14;
    setText(pdf, PALETTE.gray6);
    pdf.text('Markup', rightX - 140, cy);
    setText(pdf, PALETTE.body);
    pdf.text(markup == null ? '—' : `${markup.toFixed(0)}%`, rightX, cy, { align: 'right' });
    cy += 16;
  }
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(TYPE.h2);
  setText(pdf, PALETTE.body);
  pdf.text('Total price', rightX - 170, cy);
  pdf.text(fmtMoney(quotation.totalPrice), rightX, cy, { align: 'right' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(TYPE.body);
  return cy + 10;
}

// ============================================================================
//  Schedule / Gantt
// ============================================================================

interface ScheduleRow { label: string; variant: BadgeVariant; start: Date | null; days: number | null; end: Date | null }

interface ComputedSchedule {
  start: Date | null;
  supEnd: Date | null;
  exStart: Date | null;
  exEnd: Date | null;
  prepStart: Date | null;
  prepEnd: Date | null;
  shipStart: Date | null;
  shipEnd: Date | null;
  rows: ScheduleRow[];
}

function computeSchedule(quotation: QuotationDetail): ComputedSchedule {
  const sched = quotation.schedule;
  const start = fromISODate(sched.startDate);
  const supEnd = start && sched.supplyDays != null ? addWorkdays(start, sched.supplyDays) : null;
  const exUserStart = fromISODate(sched.executionStartDate);
  const exStart = supEnd ? (exUserStart && exUserStart > supEnd ? exUserStart : supEnd) : null;
  const exEnd = exStart && sched.executionDays != null ? addWorkdays(exStart, sched.executionDays) : null;
  const prepUserStart = fromISODate(sched.prepStartDate);
  const prepStart = exEnd ? (prepUserStart && prepUserStart > exEnd ? prepUserStart : exEnd) : null;
  const prepEnd = prepStart && sched.prepDays != null ? addWorkdays(prepStart, sched.prepDays) : null;
  const shipUserStart = fromISODate(sched.shippingStartDate);
  const shipStart = prepEnd ? (shipUserStart && shipUserStart > prepEnd ? shipUserStart : prepEnd) : null;
  const shipEnd = shipStart && sched.shippingDays != null ? addWorkdays(shipStart, sched.shippingDays) : null;

  const rows: ScheduleRow[] = [
    { label: 'Order start',       variant: 'light-orange', start,              days: 0,                      end: start   },
    { label: 'Supplying',         variant: 'light-gray',   start,              days: sched.supplyDays,       end: supEnd  },
    { label: 'Execution',         variant: 'light-orange', start: exStart,     days: sched.executionDays,    end: exEnd   },
    { label: 'Preparing to ship', variant: 'light-blue',   start: prepStart,   days: sched.prepDays,         end: prepEnd },
    { label: 'Shipping',          variant: 'light-green',  start: shipStart,   days: sched.shippingDays,     end: shipEnd },
  ];
  return { start, supEnd, exStart, exEnd, prepStart, prepEnd, shipStart, shipEnd, rows };
}

function drawSchedule(
  pdf: jsPDF, y: number, margin: number, width: number,
  quotation: QuotationDetail, design: PdfDesign,
): number {
  y = drawSectionHeader(pdf, y, 'Order Duration', width);
  y += 12;
  const s = computeSchedule(quotation);
  const timing = design.basics.timingStyle;

  if (timing === 'Closed Book') {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(TYPE.xs);
    setText(pdf, PALETTE.gray6);
    pdf.text('ESTIMATED SHIPPING DATE', margin + 10, y + 2, { charSpace: 0.4 });
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(TYPE.h2);
    setText(pdf, PALETTE.body);
    pdf.text(fmtDate(s.shipEnd ? s.shipEnd.toISOString() : null), margin + 10, y + 22);
    return y + 30;
  }

  if (timing === 'Transparent') {
    const lines: Array<{ label: string; value: string; variant: BadgeVariant }> = [
      { label: 'ORDER START',     value: fmtDate(s.start ? s.start.toISOString() : null),     variant: 'light-orange' },
      { label: 'SHIPPING (EST.)', value: fmtDate(s.shipEnd ? s.shipEnd.toISOString() : null), variant: 'light-green'  },
    ];
    for (const l of lines) {
      drawBadge(pdf, margin + 10, y, l.label, l.variant);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(TYPE.body);
      setText(pdf, PALETTE.body);
      pdf.text(l.value, margin + width - 10, y + 10, { align: 'right' });
      y += 22;
    }
    pdf.setFont('helvetica', 'normal');
    return y;
  }

  // Open Book — full 5-row table
  const bodyRows = s.rows.map((r) => [
    r.label,
    fmtDate(r.start ? r.start.toISOString() : null),
    r.days == null ? '—' : `${r.days}`,
    fmtDate(r.end ? r.end.toISOString() : null),
  ]);
  const labels = s.rows.map((r) => r.label);
  const variants = s.rows.map((r) => r.variant);
  autoTable(pdf, {
    startY: y,
    margin: { left: margin, right: margin },
    theme: 'plain',
    head: [['Step', 'Start date', 'Work days', 'End date']],
    body: bodyRows,
    styles: {
      fontSize: TYPE.small,
      cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
      textColor: PALETTE.body,
    },
    headStyles: {
      fillColor: PALETTE.gray1,
      textColor: PALETTE.gray8,
      fontSize: TYPE.xs,
      fontStyle: 'bold',
    },
    columnStyles: {
      0: { cellWidth: 140 },
      1: { cellWidth: 110 },
      2: { cellWidth: 70, halign: 'right' },
      3: { cellWidth: 'auto' },
    },
    willDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 0) data.cell.text = [];
    },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 0) {
        const by = data.cell.y + (data.cell.height - 13) / 2;
        drawBadge(pdf, data.cell.x + 8, by, labels[data.row.index] ?? '', variants[data.row.index] ?? 'light-gray');
      }
    },
  });
  return (pdf as any).lastAutoTable?.finalY ?? y;
}

// ============================================================================
//  Gantt (row-per-phase design matching OrderGanttSection SVG component)
// ============================================================================

interface GanttBar {
  label: string;
  fill: RGB;
  stroke: RGB;
  start: Date;
  end: Date;
  milestone: boolean;   // 0-day steps render as diamonds
}

function computeGanttBars(quotation: QuotationDetail): GanttBar[] {
  const sched = quotation.schedule;
  const start = fromISODate(sched.startDate);
  if (!start) return [];

  const supDays = sched.supplyDays ?? 0;
  const supEnd = addWorkdays(start, supDays);

  const exUser = fromISODate(sched.executionStartDate);
  const exStart = exUser && exUser > supEnd ? exUser : supEnd;
  const exDays = sched.executionDays ?? 0;
  const exEnd = addWorkdays(exStart, exDays);

  const prepUser = fromISODate(sched.prepStartDate);
  const prepStart = prepUser && prepUser > exEnd ? prepUser : exEnd;
  const prepDays = sched.prepDays ?? 0;
  const prepEnd = addWorkdays(prepStart, prepDays);

  const shipUser = fromISODate(sched.shippingStartDate);
  const shipStart = shipUser && shipUser > prepEnd ? shipUser : prepEnd;
  const shipDays = sched.shippingDays ?? 0;
  const shipEnd = addWorkdays(shipStart, shipDays);

  return [
    { label: 'Order Start',     fill: PALETTE.orange,      stroke: PALETTE.orange,  start, end: start,     milestone: true           },
    { label: 'Supplying',       fill: PALETTE.gray1,       stroke: PALETTE.gray6,   start, end: supEnd,     milestone: supDays === 0  },
    { label: 'In Execution',    fill: PALETTE.orangeLight, stroke: PALETTE.orange,  start: exStart,  end: exEnd,   milestone: exDays === 0   },
    { label: 'Prep. to Ship',   fill: PALETTE.blueLight,   stroke: PALETTE.blue,    start: prepStart, end: prepEnd, milestone: prepDays === 0 },
    { label: 'Shipping',        fill: PALETTE.greenLight,  stroke: PALETTE.green,   start: shipStart, end: shipEnd, milestone: shipDays === 0 },
  ];
}

/** Draw a filled diamond centred at (cx, cy) with half-size s. */
function drawDiamond(pdf: jsPDF, cx: number, cy: number, s: number, fill: RGB, stroke: RGB) {
  setFill(pdf, fill);
  setStroke(pdf, stroke);
  pdf.setLineWidth(0.8);
  // lines() uses relative [dx, dy] vectors from the given start point.
  // Start: bottom point (cx, cy+s); path clockwise.
  pdf.lines([[s, -s], [-s, -s], [-s, s], [s, s]], cx, cy + s, [1, 1], 'FD', true);
}

function drawGantt(
  pdf: jsPDF, y: number, margin: number, width: number,
  quotation: QuotationDetail,
): number {
  y = drawSectionHeader(pdf, y, 'Order Gantt', width);
  y += 14;

  const bars = computeGanttBars(quotation);
  if (bars.length === 0) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(TYPE.small);
    setText(pdf, PALETTE.gray6);
    pdf.text('No schedule defined yet.', margin + 10, y + 10);
    setText(pdf, PALETTE.body);
    pdf.setFont('helvetica', 'normal');
    return y + 20;
  }

  const first = bars[0].start;
  const last  = bars.reduce((m, b) => (b.end > m ? b.end : m), bars[0].end);
  const totalDays = Math.max(1, calendarDaysBetween(first, last));

  // Layout constants
  const leftGutter = 90;             // width of label column
  const barAreaW   = width - leftGutter;
  const pxPerDay   = Math.max(3, Math.min(18, barAreaW / totalDays));
  const rowH       = 22;
  const barH       = 12;
  const axisH      = 20;              // date-tick row height above first bar
  const chartX     = margin + leftGutter;
  const usedW      = Math.min(barAreaW, totalDays * pxPerDay);

  // Helper: x position for a date
  const xFor = (d: Date) => chartX + calendarDaysBetween(first, d) * pxPerDay;

  // ── Date axis (ticks + labels) ───────────────────────────────────────────
  const tickStep = totalDays <= 14 ? 1 : totalDays <= 60 ? 7 : 14;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6);
  setText(pdf, PALETTE.gray6);
  setStroke(pdf, PALETTE.gray3);
  pdf.setLineWidth(0.3);

  // Vertical grid lines spanning the whole chart height
  const gridBottom = y + axisH + bars.length * rowH;
  for (let i = 0; i <= totalDays; i += tickStep) {
    const x = chartX + i * pxPerDay;
    if (x > chartX + usedW + 1) break;
    pdf.line(x, y + axisH - 4, x, gridBottom);
    const d = new Date(first);
    d.setDate(d.getDate() + i);
    pdf.text(dayjs(d).format('MMM D'), x, y + axisH - 7, { align: 'center' });
  }

  // Axis baseline
  setStroke(pdf, PALETTE.gray6);
  pdf.setLineWidth(0.5);
  pdf.line(chartX, y + axisH - 2, chartX + usedW, y + axisH - 2);

  // ── Rows ────────────────────────────────────────────────────────────────
  const rowsY = y + axisH;

  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const rowTop = rowsY + i * rowH;
    const barCY  = rowTop + rowH / 2;          // vertical centre of bar
    const barTop = barCY - barH / 2;

    // Row label
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(TYPE.xs);
    setText(pdf, PALETTE.body);
    pdf.text(b.label, chartX - 4, barCY + 3, { align: 'right' });

    if (b.milestone) {
      drawDiamond(pdf, xFor(b.start), barCY, 5, b.fill, b.stroke);
    } else {
      const x1 = xFor(b.start);
      const x2 = Math.max(x1 + 2, xFor(b.end));
      const bw  = x2 - x1;
      setFill(pdf, b.fill);
      setStroke(pdf, b.stroke);
      pdf.setLineWidth(0.5);
      pdf.roundedRect(x1, barTop, bw, barH, 2, 2, 'FD');
      // Duration label inside bar when there's enough room
      if (bw > 28) {
        const days = calendarDaysBetween(b.start, b.end);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(6);
        setText(pdf, b.stroke);
        pdf.text(`${days}d`, x1 + bw / 2, barTop + barH - 3, { align: 'center' });
      }
    }
  }

  // ── Connector lines between bars ─────────────────────────────────────────
  setStroke(pdf, PALETTE.gray6);
  pdf.setLineWidth(0.4);
  for (let i = 1; i < bars.length; i++) {
    const prev = bars[i - 1];
    const curr = bars[i];
    const x1 = xFor(prev.end);
    const y1 = rowsY + (i - 1) * rowH + rowH / 2;
    const x2 = xFor(curr.start);
    const y2 = rowsY + i * rowH + rowH / 2;
    const mid = Math.max(x1 + 4, x2 - 4);
    // L-shaped dashed connector
    pdf.setLineDashPattern([2, 2], 0);
    pdf.line(x1, y1, mid, y1);
    pdf.line(mid, y1, mid, y2);
    pdf.line(mid, y2, x2 - 3, y2);
    pdf.setLineDashPattern([], 0);
    // Arrowhead
    setFill(pdf, PALETTE.gray6);
    pdf.lines([[4, -3], [-4, -3]], x2 - 3, y2, [1, 1], 'F', true);
  }

  // ── Start / end date labels under chart ──────────────────────────────────
  const dateY = gridBottom + 10;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(TYPE.xs);
  setText(pdf, PALETTE.gray6);
  pdf.text(fmtDate(first.toISOString()), chartX, dateY);
  if (last > first) {
    pdf.text(fmtDate(last.toISOString()), chartX + usedW, dateY, { align: 'right' });
  }
  setText(pdf, PALETTE.body);

  return dateY + 10;
}

// ============================================================================
//  Footer pass — runs after all drawing so total page count is known.
// ============================================================================

function drawFooters(pdf: jsPDF, uid: string, margin: number, pageWidth: number) {
  const total = pdf.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    const y = 820;
    setStroke(pdf, PALETTE.gray3);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y - 8, pageWidth - margin, y - 8);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(TYPE.xs);
    setText(pdf, PALETTE.gray6);
    pdf.text(uid, margin, y);
    pdf.text(`Page ${i} of ${total}`, pageWidth - margin, y, { align: 'right' });
    setText(pdf, PALETTE.body);
  }
}

// ============================================================================
//  Public entry point
// ============================================================================

export async function generateQuotationPdf(opts: {
  design: PdfDesign;
  quotation: QuotationDetail;
  orderParts: OrderPart[];
  order: OrderInfo;
  customer: CustomerInfo | null;
  company: CompanyInfo;
  refs: ResolvedRef;
  imageUrlFor: (rel: string | null | undefined) => string | undefined;
}): Promise<Blob> {
  const { design, quotation, orderParts, order, customer, company, refs, imageUrlFor } = opts;

  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = PAGE.margin;
  const innerWidth = pageWidth - margin * 2;

  // ---- Prefetch logos + part images + equipment icons (sync hooks can't await later). ------
  const paths: Array<string | null | undefined> = [];
  if (design.basics.includeCompanyLogo) paths.push(company.logoPath);
  if (design.basics.includeCustomerLogo) paths.push(order.customerLogo);
  if (design.basics.includePartsIcon) for (const p of orderParts) paths.push(p.imagePath);
  // Equipment icons for the process sub-table details column.
  if (design.equipment.includeEquipmentIcons) {
    for (const qp of quotation.parts) {
      for (const pr of qp.processes) {
        if (pr.equipmentId) {
          const eq = refs.equipmentById.get(pr.equipmentId);
          if (eq?.imagePath) paths.push(eq.imagePath);
        }
      }
    }
  }
  const imageCache = await prefetchImages(paths, imageUrlFor);

  // ---- 1. Document title strip -----------------------------------------
  let y = drawDocHeader(pdf, quotation, margin, pageWidth);
  y += 6;

  // ---- 2. Party blocks (company / customer) --------------------------------
  // The company (FROM) card is always shown — it carries the lab's identity.
  // The customer card is shown whenever the order has a customer attached.
  // The label *text* inside each card is conditional:
  //   • "From · TechName"         only when technicianName is set (else no label row)
  //   • "In the attention of · X" only when contactName is set   (else no label row)
  {
    const colGap = 12;
    const companyLogo = (design.basics.includeCompanyLogo && company.logoPath)
      ? imageCache.get(company.logoPath) ?? null : null;
    const hasCustomer = !!(customer || order.customerName);
    const customerLogo = (hasCustomer && design.basics.includeCustomerLogo && order.customerLogo)
      ? imageCache.get(order.customerLogo) ?? null : null;

    if (hasCustomer) {
      // Two-column layout: company on the left, customer on the right
      const colW = (innerWidth - colGap) / 2;
      const leftBottom  = drawCompanyBlock(pdf, margin, y, colW, company, order.technicianName, companyLogo);
      const rightBottom = drawCustomerBlock(pdf, margin + colW + colGap, y, colW, order, customer, customerLogo);
      y = Math.max(leftBottom, rightBottom) + 16;
    } else {
      // No customer — company card full-width
      y = drawCompanyBlock(pdf, margin, y, innerWidth, company, order.technicianName, companyLogo) + 16;
    }
  }

  // ---- 3. Parts Prototyping section ------------------------------------
  y = drawSectionHeader(pdf, y, 'Parts Prototyping', innerWidth);
  y += 10;

  // ensureSpace: parts-header version re-emits the band on continuation pages
  const ensureSpaceParts = (yIn: number, needed: number): number => {
    if (yIn + needed <= PAGE.bottomLimit) return yIn;
    pdf.addPage();
    let ny = PAGE.margin;
    ny = drawSectionHeader(pdf, ny, 'Parts Prototyping (continued)', innerWidth);
    return ny + 10;
  };
  const ensureSpacePlain = (yIn: number, needed: number): number => {
    if (yIn + needed <= PAGE.bottomLimit) return yIn;
    pdf.addPage();
    return PAGE.margin;
  };

  let zebra = false;
  for (const qp of quotation.parts) {
    const part = orderParts.find((op) => op.id === qp.orderPartId) ?? null;
    y = drawPartBlock(pdf, y, {
      part, qpart: qp, design, refs, imageCache, zebra, margin, innerWidth,
    }, ensureSpaceParts);
    zebra = !zebra;
  }

  if (quotation.parts.length === 0) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(TYPE.small);
    setText(pdf, PALETTE.gray6);
    pdf.text('No parts on this quotation yet.', margin + 10, y + 10);
    setText(pdf, PALETTE.body);
    pdf.setFont('helvetica', 'normal');
    y += 24;
  }

  // ---- 4. Shipping row (conditional) -----------------------------------
  if ((quotation.shippingCost ?? 0) > 0) {
    y = ensureSpacePlain(y + 4, 30);
    y = drawShippingRow(pdf, y, margin, innerWidth, quotation.shippingCost);
  }

  // ---- 5. Totals strip -------------------------------------------------
  y = ensureSpacePlain(y + 8, 72);
  y = drawTotalsStrip(pdf, y, margin, innerWidth, quotation, design);

  // ---- 6. Order Duration section --------------------------------------
  y = ensureSpacePlain(y + 18, 120);
  y = drawSchedule(pdf, y, margin, innerWidth, quotation, design);

  // ---- 7. Order Gantt (conditional) -----------------------------------
  if (design.basics.includeGantt) {
    y = ensureSpacePlain(y + 14, 80);
    y = drawGantt(pdf, y, margin, innerWidth, quotation);
  }

  // ---- 8. Footer pass --------------------------------------------------
  drawFooters(pdf, quotation.quotationUid, margin, pageWidth);

  return pdf.output('blob');
}
